import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CalculateIcon from '@mui/icons-material/Calculate';
import Card from "@mui/material/Card";
import EditIcon from '@mui/icons-material/Edit';
import FormatListNumberedRtlIcon from '@mui/icons-material/FormatListNumberedRtl';
import Grid from "@mui/material/Grid";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import WaterfallChartIcon from '@mui/icons-material/WaterfallChart';

import { formatString, removeCommasFromValues } from "./valueFunctions";
import { clearCostValueDriver, editVariableValues, fetchCostValueDriver, fetchValueModel, fetchViewValueModel } from "../../actions/valueActions";
import { tooltip } from "../helpers/tooltip";
import ModalElement from "../Modal";
import firstCharToUpper from "../helpers/stringFormat";

import CostValueDriverLabor from "./CostValueDriverLabor";
import CostValueDriverCS from "./CostValueDriverCS";
import CostValueDriverOperating from "./CostValueDriverOperating";
import ValueModelTree from "./ValueModelTree";
import { getIconFromDriverName } from "./iconFunctions";
import CostValueDriverRandD from "./CostValueDriverRandD";
import CostValueDriverInvestment from "./CostValueDriverInvestment";
import CostValueDriverCompliance from "./CostValueDriverCompliance";
import CostValueDriverInventory from "./CostValueDriverInventory";
import CostValueDriverFacility from "./CostValueDriverFacility";
import CostValueDriverEquipment from "./CostValueDriverEquipment";
import CostValueDriverUpgrade from "./CostValueDriverUpgrade";
import RevenueValueDriverTTM from "./RevenueValueDriverTTM";
import RevenueValueDriverSales from "./RevenueValueDriverSales";
import RevenueValueDriverMarketing from "./RevenueValueDriverMarketing";
import RevenueValueDriverCLV from "./RevenueValueDriverCLV";
import RevenueValueDriverIR from "./RevenueValueDriverIR";
import VariablesForm from "./VariablesForm";
import ViewValueModel from "./ViewValueModel";
import RiskValueDriverRegulatory from "./RiskValueDriverRegulatory";
import RiskValueDriverLitigation from "./RiskValueDriverLitigation";
import RiskValueDriverRevenue from "./RiskValueDriverRevenue";
import { formatNumberThsndAndDec } from "../helpers/stringFormat";
import history from "../../history";
import CostValueDriverOverhead from "./CostValueDriverOverhead";
import CostValueDriverPayment from "./CostValueDriverPayment";

const ValueModel = () => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const [selected, setSelected] = useState(['1.1.1'])
    const [variablesSetOpen, setVariablesSetOpen] = useState(false)
    const [valueModelModalOpen, setValueModelModalOpen] = useState(false)

    const toggleVariablesSetOpen = () => {
        setVariablesSetOpen(!variablesSetOpen)
    }

    const toggleValueModelModalOpen = () => {
        setValueModelModalOpen(!variablesSetOpen)
    }

    const handleClose = () => {
        setVariablesSetOpen(false)
        setValueModelModalOpen(false)
    }

    // Access state for valueModel and filter using searchParams
    const id = searchParams.get('id')
    const valueModel = useSelector(state => state.value.valueModel[id])
    const driver = useSelector(state => state.value.costStructure)
    const costStructure = useSelector(state => state.value.costStructure)
    const variables = useSelector(state => state.value.valueModel[id]?.variables)

    const nofValueDrivers = {
        'cost_value_driver': {
            'operational_costs': valueModel?.cost_value_driver.operating_savings.length || '',
            'capital_investment': valueModel?.cost_value_driver.investment_savings.length || '',
            'research_and_development': valueModel?.cost_value_driver.randd_savings.length || '',
            'compliance_costs': valueModel?.cost_value_driver.compliance_savings.length || '',
            'personnel_costs': valueModel?.cost_value_driver.labor_savings.length || '',
            'customer_support': valueModel?.cost_value_driver.cs_savings.length || '',
            'faster_payment': valueModel?.cost_value_driver?.payment_savings?.length || '',
            'inventory_costs': valueModel?.cost_value_driver.inventory_savings.length || '',
            'facilities_and_infrastructure': valueModel?.cost_value_driver.facility_savings.length || '',
            'production_lines_and_equipment': valueModel?.cost_value_driver.equipment_savings.length || '',
            'upgrades': valueModel?.cost_value_driver.upgrade_savings.length || '',
        },
        'revenue_value_driver': {
            'sales-driven_revenue': valueModel?.revenue_value_driver.sales_driven.length || '',
            'marketing-driven_revenue': valueModel?.revenue_value_driver.marketing_driven.length || '',
            'time_to_market': valueModel?.revenue_value_driver.time_to_market.length || '',
            'customer_lifetime_value': valueModel?.revenue_value_driver.lifetime_value.length || '',
            'incremental_revenue': valueModel?.revenue_value_driver.incremental_revenue.length || '',
        },
        'risk_value_driver': {
            'regulatory_risk': valueModel?.risk_value_driver.regulatory_reduction.length || '',
            'litigation_risk': valueModel?.risk_value_driver.litigation_reduction.length || '',
            'revenue_loss': valueModel?.risk_value_driver.revenue_loss.length || '',
        }
    }

    useEffect(() => {
        dispatch(fetchCostValueDriver(id))
        dispatch(fetchValueModel(id))
        dispatch(fetchViewValueModel(id, {}))

        return () => {
            // This is the cleanup function
            dispatch(clearCostValueDriver())
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps        
    }, [])

    const createCostTitle = (driver) => {
        return (
            <Stack direction="row" spacing={1} sx={{ mt: 3, ml: 2, mb: 2, alignItems: 'center' }}>
                <Box component={getIconFromDriverName(driver)} />
                <Typography variant="body" sx={{ fontWeight: 'bold' }}>
                    {formatString(driver)}
                </Typography>
            </Stack>
        )
    }

    const variablesOnSubmit = (values) => {
        values = removeCommasFromValues(values)
        dispatch(editVariableValues(id, values))
        handleClose()
        if (formikRef.current) {
            formikRef.current.resetForm();
        }

    }


    const variablesSetFormActions = () => {
        return (
            <React.Fragment>
                <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit" sx={{ mt: 2 }}>Save</Button>
            </React.Fragment>
        )
    }

    function renderVariablesSetModal() {
        return (
            <ModalElement
                modalOpen={variablesSetOpen}
                modalWidth={400}
                handleClose={handleClose}
                header="Set Default Values"
                instruction="Values will be the default for the Value Model"
                content={
                    <VariablesForm
                        variables={variables}
                        formActions={variablesSetFormActions}
                        onSubmit={variablesOnSubmit}
                    />
                }
            />
        )
    }

    function renderValueModelModal() {
        return (
            <ModalElement
                modalOpen={valueModelModalOpen}
                modalWidth={1400}
                handleClose={handleClose}
                header="Preview Value Model"
                instruction="The Price Range between the Price of Alternative and Your Created Value is where you should keep your price, if Your Created Value is higher. Your Created Value is always the maximum you can charge and you should probably set your price in the lower end of the range."
                content={
                    <ViewValueModel
                        id={id}
                    />
                }
            />
        )
    }

    const overheadDrivers = [
        "finance_and_accounting",
        "HR_and_people",
        "sales_and_business_development",
        "marketing_and_communications",
        "customer_success",
        "IT_and_infrastructure",
        "legal_and_compliance",
        "procurement_and_purchasing",
    ];

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                {/* Left Section with Icon and Title */}
                <Stack direction="row" alignItems="center" gap={1} sx={{ ml: 5, mt: 3 }}>
                    <CalculateIcon />
                    <Typography variant="h6">Value Models</Typography>
                    <KeyboardArrowRightIcon />
                    <Typography variant="h6">Configure Value Model</Typography>
                </Stack>

                {/* Positioned Button */}
                <Button variant="contained" startIcon={<ArrowBackIosIcon />} onClick={() => history.push(`/value-models?id=${valueModel.product.id}`)} sx={{ mr: '30%', mt: 3, textTransform: 'none' }}>
                    Back
                </Button>
            </Stack>
            <Box sx={{ maxWidth: 1200 }}>
                {valueModel && costStructure ?
                    <Paper sx={{ maxWidth: 1200, padding: 3, ml: 4, mt: 4 }}>
                        <Stack direction="row">
                            <Box
                                sx={{
                                    minHeight: '600px', // Height of the box
                                    position: 'relative', // Make the box relative for positioning the button
                                    display: 'flex', // Flexbox to position the grid and button
                                    flexDirection: 'column', // Align elements in a column
                                    justifyContent: 'space-between', // Space between top grid and bottom button
                                    padding: 2, // Optional padding for spacing
                                }}
                            >
                                <Grid container m={2} sx={{ maxWidth: 400 }}>
                                    <Grid item xs={12}>
                                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                            <CalculateIcon />
                                            <Typography variant="h6">
                                                {valueModel.name}
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            Product:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={9}>
                                        <Typography variant="caption">
                                            {firstCharToUpper(valueModel.product.name)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            Industry:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={9}>
                                        <Stack direction="row">
                                            <Typography variant="caption">
                                                {valueModel.industry}
                                                {tooltip(driver.description)}
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            Revenue model:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={9}>
                                        <Typography variant="caption">
                                            {valueModel.product.revenue_model === 'transactional' ?
                                                `${firstCharToUpper(valueModel.product.revenue_model)} with a price per ${valueModel.product.unit}`
                                                : `Subscription with a ${valueModel.product.frequency} price${valueModel.product.unit.length > 0 ? ' per ' + valueModel.product.unit : ''}`}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            Alternative:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={9}>
                                        <Typography variant="caption">
                                            {`${valueModel.product.alternative} with a cost of ${valueModel.product.currency + formatNumberThsndAndDec(valueModel.product.price_alternative)} ${valueModel.product.revenue_model === 'transactional' && valueModel.product.unit.length > 0 ? 'per ' + valueModel.product.unit : ''}${valueModel.product.revenue_model === 'subscription' && valueModel.product.unit.length > 0 ? valueModel.product.frequency + ' per ' + valueModel.product.unit : valueModel.product.frequency}`}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Box>
                                    <Stack direction="row" gap={1}>
                                        <Button startIcon={<FormatListNumberedRtlIcon />} variant="contained" size="small" onClick={toggleVariablesSetOpen} sx={{ textTransform: 'none' }}>Default Values</Button>
                                        <Button startIcon={<WaterfallChartIcon />} variant="outlined" size="small" color="alternative" onClick={toggleValueModelModalOpen} sx={{ textTransform: 'none' }}>Preview Value Model</Button>
                                    </Stack>
                                </Box>
                            </Box>
                            <ValueModelTree
                                costStructure={costStructure?.cost_structure}
                                investmentStructure={costStructure?.investment_structure}
                                productTypes={costStructure?.product_types}
                                revenueTypes={costStructure?.revenue_types}
                                selected={selected}
                                setSelected={setSelected}
                                nofValueDrivers={nofValueDrivers}
                            />
                        </Stack>
                    </Paper>
                    : null}

                {Object.keys(costStructure['cost_structure'] || {}).map((driver, index) => {
                    if (driver === 'personnel_costs' && selected.includes(`1.1.${index + 1}`)) {
                        return (
                            <Box key={driver}>
                                <Card key={driver} sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                                    {createCostTitle(driver)}
                                    <CostValueDriverLabor
                                        id={id}
                                        valueModel={valueModel}
                                        costs={costStructure['cost_structure'][driver]}
                                        driver={driver}
                                    />
                                </Card>
                            </Box>
                        )
                    }
                    else if (overheadDrivers.includes(driver) && selected.includes(`1.1.${index + 1}`)) {
                        return (
                            <Box key={driver}>
                                <Card key={driver} sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                                    {createCostTitle(driver)}
                                    <CostValueDriverOverhead
                                        id={id}
                                        valueModel={valueModel}
                                        costs={costStructure['cost_structure'][driver]}
                                        driver={driver}
                                    />
                                </Card>
                            </Box>
                        )
                    }
                    else if (driver === 'customer_support' && selected.includes(`1.1.${index + 1}`)) {
                        return (
                            <Box key={driver}>
                                <Card key={driver} sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                                    {createCostTitle(driver)}
                                    <CostValueDriverCS
                                        id={id}
                                        valueModel={valueModel}
                                        costs={costStructure['cost_structure'][driver]}
                                        driver={driver}
                                    />
                                </Card>
                            </Box>
                        )
                    }
                    else if (driver === 'operational_costs' && selected.includes(`1.1.${index + 1}`)) {
                        return (
                            <Box key={driver}>
                                <Card key={driver} sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                                    {createCostTitle(driver)}
                                    <CostValueDriverOperating
                                        id={id}
                                        valueModel={valueModel}
                                        costs={costStructure['cost_structure'][driver]}
                                        driver={driver}
                                        filterCosts={['warehouse_storage']}
                                    />
                                </Card>
                            </Box>
                        )
                    }
                    else if (driver === 'research_and_development' && selected.includes(`1.1.${index + 1}`)) {
                        return (
                            <Box key={driver}>
                                <Card key={driver} sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                                    {createCostTitle(driver)}
                                    <CostValueDriverRandD
                                        id={id}
                                        valueModel={valueModel}
                                        costs={costStructure['cost_structure'][driver]}
                                        driver={driver}
                                    />
                                </Card>
                            </Box>
                        )
                    }
                    else if (driver === 'capital_investment' && selected.includes(`1.1.${index + 1}`)) {
                        return (
                            <Box key={driver}>
                                <Card key={driver} sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                                    {createCostTitle(driver)}
                                    <CostValueDriverInvestment
                                        id={id}
                                        valueModel={valueModel}
                                        costs={costStructure['cost_structure'][driver]}
                                        driver={driver}
                                    />
                                </Card>
                            </Box>
                        )
                    }
                    else if (driver === 'compliance_costs' && selected.includes(`1.1.${index + 1}`)) {
                        return (
                            <Box key={driver}>
                                <Card key={driver} sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                                    {createCostTitle(driver)}
                                    <CostValueDriverCompliance
                                        id={id}
                                        valueModel={valueModel}
                                        costs={costStructure['cost_structure'][driver]}
                                        driver={driver}
                                    />
                                </Card>
                            </Box>
                        )
                    }
                    else {
                        return null
                    }
                })}
                {selected.includes('1.2.1') &&
                    <Box key="inventory">
                        <Card key="inventory" sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                            {createCostTitle("inventory")}
                            <CostValueDriverInventory
                                id={id}
                                valueModel={valueModel}
                                productionCosts={costStructure['cost_structure']['operational_costs']['warehouse_storage']['production_costs']} //costStructure['costs_sum']['production_costs']
                                warehouseCosts={costStructure['cost_structure']['operational_costs']['warehouse_storage']}
                                driver={'inventory_costs'}
                                cost={'warehouse_storage'}
                            />
                        </Card>
                    </Box>
                }
                {selected.includes('1.2.2') &&
                    <Box key="faster_payment">
                        <Card key="faster_payment" sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                            {createCostTitle("faster_payment")}
                            <CostValueDriverPayment
                                id={id}
                                valueModel={valueModel}
                                driver={'faster_payment'}
                            />
                        </Card>
                    </Box>
                }
                {Object.keys(costStructure['investment_structure'] || {}).map((driver, index) => {
                    if (driver === 'facilities_and_infrastructure' && selected.includes(`1.3.${index + 1}`)) {
                        return (
                            <Box key={driver}>
                                <Card key={driver} sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                                    {createCostTitle(driver)}
                                    <CostValueDriverFacility
                                        id={id}
                                        valueModel={valueModel}
                                        costs={costStructure['investment_structure'][driver]}
                                        driver={driver}
                                    />
                                </Card>
                            </Box>
                        )
                    }
                    else if ((driver === 'production_lines_and_equipment' || driver === 'tools_and_equipment') && selected.includes(`1.3.${index + 1}`)) {
                        return (
                            <Box key={driver}>
                                <Card key={driver} sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                                    {createCostTitle(driver)}
                                    <CostValueDriverEquipment
                                        id={id}
                                        valueModel={valueModel}
                                        costs={costStructure['investment_structure'][driver]}
                                        driver={driver}
                                    />
                                </Card>
                            </Box>
                        )
                    }
                    else if (driver === 'upgrades' && selected.includes(`1.3.${index + 1}`)) {
                        return (
                            <Box key={driver}>
                                <Card key={driver} sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                                    {createCostTitle(driver)}
                                    <CostValueDriverUpgrade
                                        id={id}
                                        valueModel={valueModel}
                                        costs={costStructure['investment_structure'][driver]}
                                        driver={driver}
                                    />
                                </Card>
                            </Box>
                        )
                    }
                    else {
                        return null
                    }
                })}
                {selected.includes('2.1') &&
                    <Box key="sales">
                        <Card key="sales" sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                            {createCostTitle("sales-driven_revenue")}
                            <RevenueValueDriverSales
                                id={id}
                                valueModel={valueModel}
                                driver={'sales-driven_revenue'}
                            />
                        </Card>
                    </Box>
                }
                {selected.includes('2.2') &&
                    <Box key="marketing">
                        <Card key="marketing" sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                            {createCostTitle("marketing-driven_revenue")}
                            <RevenueValueDriverMarketing
                                id={id}
                                valueModel={valueModel}
                                driver={'marketing-driven_revenue'}
                            />
                        </Card>
                    </Box>
                }
                {selected.includes('2.3') &&
                    <Box key="ttm">
                        <Card key="ttm" sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                            {createCostTitle("time_to_market")}
                            <RevenueValueDriverTTM
                                id={id}
                                valueModel={valueModel}
                                products={costStructure['product_types']}
                                driver={'time_to_market'}
                            />
                        </Card>
                    </Box>
                }
                {selected.includes('2.4') &&
                    <Box key="npm">
                        <Card key="npm" sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                            {createCostTitle("incremental_revenue")}
                            <RevenueValueDriverIR
                                id={id}
                                valueModel={valueModel}
                                revenueTypes={costStructure['revenue_types']}
                                driver={'incremental_revenue'}
                            />
                            {/* <InventoryValueDrivers
                            id={id}
                            valueModel={valueModel}
                            productionCosts={costStructure['costs_sum']['production_costs']}
                            warehouseCosts={costStructure['cost_structure']['operational_costs']['warehouse_storage']}
                            driver={'inventory_costs'}
                            cost={'warehouse_storage'}
                        /> */}
                        </Card>
                    </Box>
                }
                {selected.includes('2.5') &&
                    <Box key="clv">
                        <Card key="clv" sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                            {createCostTitle("customer_lifetime_value")}
                            <RevenueValueDriverCLV
                                id={id}
                                valueModel={valueModel}
                                driver={'customer_lifetime_value'}
                            />
                        </Card>
                    </Box>
                }
                {selected.includes('3.1') &&
                    <Box key="regulatory">
                        <Card key="regulatory" sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                            {createCostTitle("regulatory_fines/penalties")}
                            <RiskValueDriverRegulatory
                                id={id}
                                valueModel={valueModel}
                                risks={costStructure['risk_structure']['regulatory_fines']}
                                driver={'regulatory'}
                            />
                        </Card>
                    </Box>
                }
                {selected.includes('3.2') &&
                    <Box key="litigation">
                        <Card key="litigation" sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                            {createCostTitle("litigation_risks")}
                            <RiskValueDriverLitigation
                                id={id}
                                valueModel={valueModel}
                                risks={costStructure['risk_structure']['litigation_risk']}
                                driver={'litigation'}
                            />
                        </Card>
                    </Box>
                }
                {selected.includes('3.3') &&
                    <Box key="revenue_loss">
                        <Card key="revenue_loss" sx={{ ml: 4, mt: 4, maxWidth: 1200 }}>
                            {createCostTitle("revenue_loss")}
                            <RiskValueDriverRevenue
                                id={id}
                                valueModel={valueModel}
                                driver={'revenue_loss'}
                            />
                        </Card>
                    </Box>
                }
                {renderVariablesSetModal()}
                {renderValueModelModal()}
            </Box>
        </Box>
    )
}

export default ValueModel
